import { SectionHeading } from './SectionHeading';
import {
  PRIMARY_CONTACT_LABEL,
  PRIMARY_CONTACT_LOCKED_HINT,
  PRIMARY_CONTACT_TOGGLE_DESCRIPTION
} from './memberSheetCopy';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import type { useMemberSheetForm } from './useMemberSheetForm';

const TOGGLE_ROW_CLASS = 'rounded-xl border border-border/60 p-3';

type MemberForm = ReturnType<typeof useMemberSheetForm>['form'];

type Props = {
  form: MemberForm;
  translations: NotificationGroupOverviewTranslations;
  canAssignMembershipRole: boolean;
  canEditAdmin: boolean;
  canManageAdmin: boolean;
  canManagePrimaryContact: boolean;
  isOnlyAdmin: boolean;
  memberIsPrimaryContact: boolean;
};

/** Admin, employee, and primary contact. Employee exists only for a leasing company or dealership. */
export const MemberRoleSection = ({
  form,
  translations: t,
  canAssignMembershipRole,
  canEditAdmin,
  canManageAdmin,
  canManagePrimaryContact,
  isOnlyAdmin,
  memberIsPrimaryContact
}: Props) => (
  <section className='flex flex-col gap-3'>
    <SectionHeading icon='ShieldCheck'>{t.role}</SectionHeading>
    <div className={TOGGLE_ROW_CLASS}>
      <form.AppField name='isAdmin'>
        {(field) => (
          <field.Switch
            label={t.groupAdmin}
            description={canAssignMembershipRole ? t.holderGroupAdminDescription : t.groupAdminDescription}
            switchPosition='end'
            disabled={!canEditAdmin}
          />
        )}
      </form.AppField>
    </div>
    {canAssignMembershipRole && (
      <form.Subscribe selector={(state) => state.values.isAdmin}>
        {(isAdmin) => (
          <div className={TOGGLE_ROW_CLASS}>
            <form.AppField name='isEmployee'>
              {(field) => (
                <field.Switch
                  label={t.employee}
                  description={t.employeeDescription}
                  switchPosition='end'
                  disabled={!canManageAdmin || isAdmin}
                />
              )}
            </form.AppField>
          </div>
        )}
      </form.Subscribe>
    )}
    {canManagePrimaryContact && (
      <div className={TOGGLE_ROW_CLASS}>
        <form.AppField name='isPrimaryContact'>
          {(field) => (
            <field.Switch
              label={PRIMARY_CONTACT_LABEL}
              description={memberIsPrimaryContact ? PRIMARY_CONTACT_LOCKED_HINT : PRIMARY_CONTACT_TOGGLE_DESCRIPTION}
              switchPosition='end'
              // The backend refuses to leave the group without one, so it moves by naming a successor.
              disabled={memberIsPrimaryContact}
            />
          )}
        </form.AppField>
      </div>
    )}
    {canManageAdmin && isOnlyAdmin && <p className='text-sm text-muted-foreground'>{t.lastAdminHint}</p>}
  </section>
);
